import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** The single in-flight SWF/FLV conversion. */
export interface IResourceConvertProgress {
    resourceId: string;    // the ORIGINAL swf/flv resource being converted
    resourceName: string;  // its display name (for the "converting X…" hint)
    percent: number;       // 0..100
    phase: string;         // 'downloading' | 'rendering' | 'encoding' | 'uploading' | ...
}

/**
 * App-wide state for the one-at-a-time resource conversion (SWF/FLV -> PNG/WebM/MP4).
 *
 * The conversion is DRIVEN by the resource-props panel, but its progress must be VISIBLE in the
 * resource list (an overlay on the exact tile being converted) and it must BLOCK a second convert
 * from starting elsewhere. A component-local flag can't do that, so the state lives here — provided
 * in root so every consumer shares the one instance.
 */
@Injectable({ providedIn: 'root' })
export class ResourceConvertService {
    readonly progress$ = new BehaviorSubject<IResourceConvertProgress | null>(null);

    /** true while a conversion is running (anywhere). */
    get isConverting(): boolean {
        return this.progress$.value != null;
    }

    /** the original resource id currently converting, or null when idle. */
    get convertingResourceId(): string | null {
        return this.progress$.value ? this.progress$.value.resourceId : null;
    }

    /** begin a conversion for one resource (idempotent-safe: last start wins). */
    start(resourceId: string | number, resourceName: string): void {
        this.progress$.next({
            resourceId: String(resourceId),
            resourceName: resourceName || '',
            percent: 0,
            phase: 'starting',
        });
    }

    /** push live progress; ignored if no conversion is active. */
    update(percent: number, phase: string): void {
        const cur = this.progress$.value;
        if (!cur) return;
        this.progress$.next({
            ...cur,
            percent: Math.max(0, Math.min(100, Math.round(percent || 0))),
            phase: phase || cur.phase,
        });
    }

    /** conversion finished (success or failure) -> back to idle. */
    done(): void {
        if (this.progress$.value != null) this.progress$.next(null);
    }
}
